import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Descriptions, Input, Progress, Result, Space, Typography } from 'antd';

export interface InvitationDetails {
  inviterName: string;
  inviteeEmail: string;
  companyName: string;
  role: string;
  expiresAt: string;
}

export interface InvitationAcceptFormProps {
  token: string;
  onVerifyToken: (token: string) => Promise<InvitationDetails>;
  onAcceptInvitation: (input: { token: string; password: string }) => Promise<void>;
  onSuccessRedirect?: () => void;
  termsUrl?: string;
  className?: string;
  autoRedirectDelayMs?: number;
}

type Status = 'loading' | 'ready' | 'error' | 'submitting' | 'success';

interface Rule {
  id: string;
  label: string;
  validate: (value: string) => boolean;
}

const PASSWORD_RULES: Rule[] = [
  {
    id: 'length',
    label: '8文字以上100文字以内',
    validate: (value: string) => value.length >= 8 && value.length <= 100,
  },
  {
    id: 'uppercase',
    label: '大文字を含む',
    validate: (value: string) => /[A-Z]/.test(value),
  },
  {
    id: 'lowercase',
    label: '小文字を含む',
    validate: (value: string) => /[a-z]/.test(value),
  },
  {
    id: 'number',
    label: '数字を含む',
    validate: (value: string) => /\d/.test(value),
  },
];

const DEFAULT_REDIRECT_DELAY = 3000;

export default function InvitationAcceptForm({
  token,
  onVerifyToken,
  onAcceptInvitation,
  onSuccessRedirect,
  termsUrl = '/legal/terms-privacy',
  className,
  autoRedirectDelayMs = DEFAULT_REDIRECT_DELAY,
}: InvitationAcceptFormProps) {
  const [status, setStatus] = useState<Status>('loading');
  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const errorRef = useRef<HTMLDivElement | null>(null);
  const successRef = useRef<HTMLDivElement | null>(null);
  const isMountedRef = useRef(true);
  const verifyTokenRef = useRef(onVerifyToken);
  const { Text, Paragraph } = Typography;
  const isSubmitting = status === 'submitting';

  useEffect(() => {
    verifyTokenRef.current = onVerifyToken;
  }, [onVerifyToken]);

  useEffect(() => {
    isMountedRef.current = true;
    const verify = async () => {
      try {
        setStatus('loading');
        setVerifyError(null);
        const invitation = await verifyTokenRef.current(token);
        if (!isMountedRef.current) {
          return;
        }
        setDetails(invitation);
        setStatus('ready');
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        setVerifyError(
          error instanceof Error ? error.message : '招待情報の取得に失敗しました。',
        );
        setStatus('error');
      }
    };

    verify();

    return () => {
      isMountedRef.current = false;
    };
  }, [token]);

  useEffect(() => {
    if ((status === 'error' || status === 'submitting') && submitError && errorRef.current) {
      errorRef.current.focus();
    }
  }, [status, submitError]);

  useEffect(() => {
    if (status === 'error' && verifyError && errorRef.current) {
      errorRef.current.focus();
    }
  }, [status, verifyError]);

  useEffect(() => {
    if (status === 'success' && successRef.current) {
      successRef.current.focus();
    }
  }, [status]);

  useEffect(() => {
    if (status !== 'success' || !onSuccessRedirect) {
      return;
    }

    const timer = setTimeout(() => {
      onSuccessRedirect();
    }, autoRedirectDelayMs);

    return () => {
      clearTimeout(timer);
    };
  }, [status, onSuccessRedirect, autoRedirectDelayMs]);

  const ruleStatuses = useMemo(
    () =>
      PASSWORD_RULES.map((rule) => ({
        ...rule,
        isValid: rule.validate(password),
      })),
    [password],
  );

  const confirmMatches = useMemo(
    () => confirmPassword.length > 0 && password === confirmPassword,
    [password, confirmPassword],
  );

  const satisfiedCount = useMemo(
    () => ruleStatuses.filter((rule) => rule.isValid).length,
    [ruleStatuses],
  );

  const strengthPercent = useMemo(() => {
    const total = PASSWORD_RULES.length + 1;
    const matches = satisfiedCount + (confirmMatches ? 1 : 0);
    return Math.round((matches / total) * 100);
  }, [confirmMatches, satisfiedCount]);

  const passwordValid = useMemo(() => {
    const rulesValid = ruleStatuses.every((rule) => rule.isValid);
    return rulesValid && confirmMatches;
  }, [ruleStatuses, confirmMatches]);

  const canSubmit =
    status === 'ready' &&
    passwordValid &&
    termsAccepted &&
    password.length > 0 &&
    confirmPassword.length > 0;

  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value);
    setSubmitError(null);
  }, []);

  const handleConfirmChange = useCallback((value: string) => {
    setConfirmPassword(value);
    setSubmitError(null);
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!canSubmit) {
        return;
      }

      setSubmitError(null);
      setStatus('submitting');

      try {
        await onAcceptInvitation({ token, password });
        if (!isMountedRef.current) {
          return;
        }
        setStatus('success');
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        const message =
          error instanceof Error ? error.message : '招待の受諾に失敗しました。';
        setSubmitError(message);
        setStatus('ready');
      }
    },
    [canSubmit, onAcceptInvitation, password, token],
  );

  if (status === 'loading') {
    return (
      <div className={className}>
        <Alert
          role="status"
          aria-label="招待を確認中"
          message="招待を確認中"
          description="数秒お待ちください..."
          type="info"
          showIcon
        />
      </div>
    );
  }

  if (status === 'error' && verifyError) {
    return (
      <div className={className}>
        <div ref={errorRef} role="alert" tabIndex={-1}>
          <Alert message={verifyError} type="error" showIcon />
        </div>
        <Space style={{ marginTop: 16 }}>
          <Button
            type="primary"
            onClick={() => {
              window.location.href = 'mailto:support@example.com';
            }}
          >
            サポートに連絡する
          </Button>
          <Button
            onClick={() => {
              window.location.href = '/login';
            }}
          >
            ログインページへ戻る
          </Button>
        </Space>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className={className}>
        <div ref={successRef} role="status" aria-label="招待を受諾しました" tabIndex={-1}>
          <Result
            status="success"
            title="招待を受諾しました"
            subTitle="ログインページへリダイレクトします。"
          />
        </div>
      </div>
    );
  }

  if (!details) {
    return null;
  }

  const { inviterName, inviteeEmail, companyName, role } = details;

  return (
    <form className={className} onSubmit={handleSubmit} noValidate>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', padding: 16, borderRadius: 8 }}>
          <Paragraph style={{ marginBottom: 12, fontWeight: 600 }}>
            {inviterName} からのご招待です。
          </Paragraph>
          <Descriptions size="small" column={1} colon={false}>
            <Descriptions.Item label="メールアドレス">{inviteeEmail}</Descriptions.Item>
            <Descriptions.Item label="会社">{companyName}</Descriptions.Item>
            <Descriptions.Item label="ロール">{role}</Descriptions.Item>
          </Descriptions>
        </div>

        <div>
          <label htmlFor="invitation-password" style={{ display: 'block', marginBottom: 6 }}>
            パスワード
          </label>
          <Input.Password
            id="invitation-password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => handlePasswordChange(event.target.value)}
            placeholder="新しいパスワード"
            size="large"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label htmlFor="invitation-password-confirm" style={{ display: 'block', marginBottom: 6 }}>
            パスワード（確認）
          </label>
          <Input.Password
            id="invitation-password-confirm"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => handleConfirmChange(event.target.value)}
            placeholder="確認用パスワード"
            size="large"
            disabled={isSubmitting}
          />
        </div>

        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={strengthPercent}
        >
          <Progress
            percent={strengthPercent}
            showInfo={false}
          />
        </div>

        <div style={{ background: '#f0f5ff', border: '1px solid #d6e4ff', padding: 12, borderRadius: 8 }}>
          <Text strong style={{ color: '#2f54eb' }}>パスワードの要件</Text>
          <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12, color: '#1d39c4' }}>
            {ruleStatuses.map((rule) => (
              <li
                key={rule.id}
                data-valid={rule.isValid ? 'true' : 'false'}
                style={{ color: rule.isValid ? '#52c41a' : '#1d39c4' }}
              >
                {rule.isValid ? 'OK ' : '- '} {rule.label}
              </li>
            ))}
            <li
              data-valid={confirmMatches ? 'true' : 'false'}
              style={{ color: confirmMatches ? '#52c41a' : '#1d39c4' }}
            >
              {confirmMatches ? 'OK ' : '- '}確認用パスワードと一致する
            </li>
          </ul>
        </div>

        {submitError ? (
          <div ref={errorRef} role="alert" tabIndex={-1}>
            <Alert message={submitError} type="error" showIcon />
          </div>
        ) : null}

        <Checkbox
          checked={termsAccepted}
          onChange={(event) => setTermsAccepted(event.target.checked)}
          disabled={isSubmitting}
        >
          利用規約に同意します{' '}
          <a href={termsUrl} target="_blank" rel="noopener noreferrer">
            利用規約を読む
          </a>
        </Checkbox>

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          disabled={!canSubmit}
          loading={isSubmitting}
        >
          アカウントを作成
        </Button>
      </Space>
    </form>
  );
}
